import React, { useState, useEffect, useRef } from "react";
import { Check, X, XCircle, Star, Clock, ChevronRight } from "lucide-react";
import ResizableComponent from "./ResizeableComponent";
import { defaultSizes } from "./QuizTheme";
import FeedbackIndicator from "./FeedbackIndicator";
import {
  SingleChoiceAnswer,
  MultiChoiceAnswer,
  TrueFalseAnswer,
  FormattedTextDisplay,
  NumericSliderAnswer,
  DiscreteSliderAnswer,
  MatchingPairsAnswer,
  ShapeAnswer,
  RankingAnswer,
} from "./QuizAnswer";
import { getShuffledOrder } from "./QuizUtils";
import MinimalCurvedLineComponent from "../Form/staticComponent/curvedLine";
import ShapeCheckbox from "../Form/staticComponent/shapeCheckBox";

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 600;

const QuestionCard = ({
  question,
  index,
  answers,
  updateAnswer,
  validationResults,
  attemptsUsed,
  questionScores,
  submittingQuestions,
  handleSubmitQuestion,
  sessionSettings,
  timeIsUp,
}) => {
  const [canvasScale, setCanvasScale] = useState(1);
  const containerRef = useRef(null);

  // Container-based canvas scale (mirrors QuizOverview)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateScale = () => {
      const rect = el.getBoundingClientRect();
      const widthScale = rect.width / CANVAS_WIDTH;
      const newScale = Math.min(widthScale, 1);
      setCanvasScale(Math.max(0.3, newScale));
    };
    updateScale();
    const ro = new ResizeObserver(updateScale);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Orientation message removed

  console.log(question, "here is quesion");
  const used = attemptsUsed[question.id] || 0;
  const attemptsLeft = Math.max(question.maxAttempts - used, 0);
  const alreadyCorrect = !!questionScores[question.id];
  const submitDisabled = attemptsLeft <= 0 || alreadyCorrect || timeIsUp;

  const backgroundColor = question.backgroundColor || "white";

  return (
    <div
      className="rounded-2xl shadow-md overflow-hidden transition-all hover:shadow-lg border border-gray-100 relative animate-slideIn"
      style={{
        animationDelay: `${index * 0.1}s`,
        backgroundColor: backgroundColor,
      }}
    >
      {/* Orientation overlay removed */}

      <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 p-3 sm:p-6 border-b border-indigo-100">
        <div className="flex justify-between items-start">
          <div>
            <div className="inline-flex items-center gap-2 mb-1 sm:mb-2 text-indigo-700 bg-white px-2 py-1 sm:px-3 sm:py-1 rounded-full shadow-sm border border-indigo-200">
              <span className="font-medium text-sm sm:text-base">
                Question {index + 1}
              </span>
              <span className="text-xs bg-indigo-100 px-1 py-0.5 sm:px-2 sm:py-0.5 rounded-full">
                {question.points} pts
              </span>
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-800">
              {question.title || `Question ${index + 1}`}
            </h2>
          </div>

          <div className="flex flex-col items-end">
            <div className="text-xs sm:text-sm text-gray-600 mb-1">
              {alreadyCorrect ? (
                <span className="text-emerald-600 font-medium">
                  {questionScores[question.id]} points earned
                </span>
              ) : (
                <span>
                  Attempts:{" "}
                  <span className="font-medium">
                    {used}/{question.maxAttempts}
                  </span>
                </span>
              )}
            </div>
            {!alreadyCorrect && (
              <div className="flex space-x-1">
                {Array.from({ length: question.maxAttempts }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
                      i < used ? "bg-gray-400" : "bg-gray-200"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {alreadyCorrect && (
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/30 to-emerald-600/30 backdrop-blur-sm flex items-center justify-center z-20">
          <div className="bg-white rounded-xl p-6 shadow-lg text-center max-w-sm mx-auto transform transition-all">
            <div className="rounded-full bg-emerald-100 w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-emerald-700 mb-2">
              Well done!
            </h3>
            <p className="text-emerald-600 mb-4">
              You earned {questionScores[question.id]} point
              {questionScores[question.id] !== 1 ? "s" : ""} in {used} attempt
              {used !== 1 ? "s" : ""} out of {question.maxAttempts}.
            </p>
            <div className="inline-flex items-center text-sm font-medium text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full">
              <Star size={16} className="mr-1" />
              <span>Completed</span>
            </div>
          </div>
        </div>
      )}

      {!alreadyCorrect && attemptsLeft <= 0 && (
        <div className="absolute inset-0 bg-gradient-to-r from-red-500/30 to-red-600/30 backdrop-blur-sm flex items-center justify-center z-20">
          <div className="bg-white rounded-xl p-6 shadow-lg text-center max-w-sm mx-auto">
            <div className="rounded-full bg-red-100 w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} className="text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-red-700 mb-2">
              No more attempts
            </h3>
            <p className="text-red-600 mb-4">
              You've used all {question.maxAttempts} attempts for this question.
            </p>
            <div className="inline-flex items-center text-sm font-medium text-red-700 bg-red-50 px-3 py-1 rounded-full">
              <X size={16} className="mr-1" />
              <span>No attempts left</span>
            </div>
          </div>
        </div>
      )}

      <div className="relative px-0 py-1 flex justify-center canvas-wrapper">
        <div
          ref={containerRef}
          className="mx-auto relative canvas-container flex justify-center items-center"
          style={{
            minHeight: "150px",
            width: "100%",
            maxWidth: `${CANVAS_WIDTH}px`,
            height: `${Math.ceil(CANVAS_HEIGHT * canvasScale)}px`,
            backgroundColor: backgroundColor,
            overflow: "visible",
          }}
        >
          <div
            style={{
              position: "absolute",
              width: `${CANVAS_WIDTH}px`,
              height: `${CANVAS_HEIGHT}px`,
              transform: `scale(${canvasScale})`,
              transformOrigin: "top left",
              left: 0,
              top: 0,
            }}
          >
            {question.components?.map((comp, compIndex) => {
              const compResult = validationResults[question.id]?.[comp.id];
              const renderFeedback = (
                <FeedbackIndicator
                  result={compResult}
                  showHints={sessionSettings.showComponentHints}
                />
              );

              if (comp.type === "text") {
                // console.log(comp, "here is comp")
                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={defaultSizes.text.width}
                    defaultHeight={defaultSizes.text.height}
                    animationDelay={compIndex}
                  >
                    <div
                      className="w-full h-full p-1"
                      style={{
                        fontFamily:
                          comp.text?.format?.font || "Inter, sans-serif",
                      }}
                    >
                      <FormattedTextDisplay
                        text={comp.text}
                        compValues={comp}
                      />
                    </div>
                  </ResizableComponent>
                );
              }

              if (comp.type === "image_upload") {
                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={defaultSizes.image_upload.width}
                    defaultHeight={defaultSizes.image_upload.height}
                    extraStyle={{ overflow: "hidden", borderRadius: "0.75rem" }}
                    animationDelay={compIndex}
                  >
                    {comp.image ? (
                      <img
                        src={comp.image}
                        alt="Question"
                        className="w-full h-full object-cover rounded-lg shadow-sm"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 rounded-lg border border-gray-200">
                        No Image
                      </div>
                    )}
                    {compResult && renderFeedback}
                  </ResizableComponent>
                );
              }

              if (comp.type === "line") {
                return <MinimalCurvedLineComponent key={comp.id} {...comp} />;
              }

              if (comp.type === "multiple_choice_single") {
                const val = answers[question.id]?.[comp.id] ?? null;
                console.log("multiple_choice_single", comp);
                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={defaultSizes.multiple_choice_single.width}
                    defaultHeight={defaultSizes.multiple_choice_single.height}
                    extraStyle={{
                      height: comp.height - 52 + comp.optionBorderWidth * 2,
                    }}
                    animationDelay={compIndex}
                  >
                    <div
                      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col"
                      style={{
                        backgroundColor: comp.backgroundColor || "#ffffff",
                        borderColor: comp.optionBorderColor || "#e5e7eb",
                        borderWidth: comp.optionBorderWidth || 1,
                      }}
                    >
                      {/* <div className="p-3 border-b border-gray-100 bg-gray-50">
                        <span className="text-xs font-medium text-gray-500">
                          Select one option
                        </span>
                      </div> */}
                      <div className="flex-1 overflow-auto p-2">
                        <SingleChoiceAnswer
                          value={val}
                          onChange={(newVal) =>
                            updateAnswer(question.id, comp.id, newVal)
                          }
                          compValues={comp}
                          options={comp.options || []}
                        />
                      </div>
                    </div>
                    {compResult && renderFeedback}
                  </ResizableComponent>
                );
              }

              if (comp.type === "multiple_choice_multi") {
                const val = answers[question.id]?.[comp.id] || [];
                console.log(comp, "comp here");
                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={defaultSizes.multiple_choice_multi.width}
                    defaultHeight={defaultSizes.multiple_choice_multi.height}
                    extraStyle={{
                      height: comp.height - 52 + comp.optionBorderWidth * 2,
                    }}
                    animationDelay={compIndex}
                  >
                    <div
                      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col"
                      style={{
                        backgroundColor: comp.backgroundColor || "#ffffff",
                        borderColor: comp.optionBorderColor || "#e5e7eb",
                        borderWidth: comp.optionBorderWidth || 1,
                      }}
                    >
                      {/* <div className="p-3 border-b border-gray-100 bg-gray-50">
                        <span className="text-xs font-medium text-gray-500">
                          Select multiple options
                        </span>
                      </div> */}
                      <div className="flex-1 overflow-auto p-2">
                        <MultiChoiceAnswer
                          value={val}
                          onChange={(newVal) =>
                            updateAnswer(question.id, comp.id, newVal)
                          }
                          compValues={comp}
                          options={comp.options || []}
                        />
                      </div>
                    </div>
                    {compResult && renderFeedback}
                  </ResizableComponent>
                );
              }

              if (comp.type === "true_false") {
                const val = answers[question.id]?.[comp.id];

                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={comp.width}
                    defaultHeight={comp.height}
                    animationDelay={compIndex}
                  >
                    <div
                      className="w-full h-full flex items-center justify-center bg-white rounded-xl shadow-sm p-2"
                      style={{
                        minWidth: comp.width,
                        minHeight: comp.height,
                        borderRadius: comp.borderRadius || "0.5rem",
                        borderColor: comp.borderColor || "#e5e7eb",
                        backgroundColor: comp.backgroundColor || "#f9fafb",
                      }}
                    >
                      <TrueFalseAnswer
                        value={val}
                        compValues={comp}
                        onChange={(val2) =>
                          updateAnswer(question.id, comp.id, val2)
                        }
                      />
                    </div>
                    {compResult && renderFeedback}
                  </ResizableComponent>
                );
              }

              if (comp.type === "short_text_answer") {
                const val = answers[question.id]?.[comp.id] || "";
                const count = val.length;
                console.log("short_text_answer", comp);
                const textDecoration = [
                  comp?.fontStyles.underline ? "underline" : "",
                  comp?.fontStyles.lineThrough ? "line-through" : "",
                ];
                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={comp.width + 20}
                    defaultHeight={comp.height + 20}
                    animationDelay={compIndex}
                  >
                    <div
                      className="bg-white rounded-md border border-gray-200 relative"
                      style={{
                        width: comp.width,
                        height: comp.height,
                        boxSizing: "border-box",
                        padding: 5,
                        border: "2px solid #ccc",
                        overflow: "hidden",
                      }}
                    >
                      <textarea
                        className="w-full h-full resize-none border-none bg-gray-100 text-gray-700"
                        value={val}
                        onChange={(e) =>
                          updateAnswer(question.id, comp.id, e.target.value)
                        }
                        maxLength={200}
                        placeholder="Type your answer..."
                        style={{
                          width: "100%",
                          height: "100%",
                          boxSizing: "border-box",
                          fontSize: `${!val ? 14 : comp.fontSize}px`,
                          lineHeight: comp.lineSpacing || 1.5,
                          letterSpacing: comp.letterSpacing || 0,
                          fontFamily: comp.fontFamily || "Inter, sans-serif",
                          textAlign: comp.textAlign || "left",
                          fontStyle: comp.fontStyles.italic
                            ? "italic"
                            : "normal",
                          fontWeight: comp.fontStyles.bold ? "bold" : "normal",
                          color: comp.fontColor || "#374151",
                          outline: "none",
                          overflow: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          backgroundColor: "#f9fafb",
                          borderRadius: "0.5rem",
                          padding: 3,
                          textDecoration: textDecoration.join(" "),
                        }}
                      />

                      {/* character counter */}
                      <div
                        className="absolute bottom-1 right-4  text-xs text-gray-500 pointer-events-none select-none"
                        style={{ fontSize: 10 }}
                      >
                        {count}/200
                      </div>
                    </div>
                    {compResult && renderFeedback}
                  </ResizableComponent>
                );
              }

              if (comp.type === "single_checkbox") {
                const userVal = answers[question.id]?.[comp.id];
                const checked = userVal === undefined ? false : userVal;
                console.log("single_checkbox", comp, checked);
                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={defaultSizes.single_checkbox.width}
                    defaultHeight={defaultSizes.single_checkbox.height}
                    animationDelay={compIndex}
                  >
                    <ShapeCheckbox
                      id={comp.id}
                      checked={checked}
                      onChange={(e) =>
                        updateAnswer(question.id, comp.id, e.target.checked)
                      }
                      width={comp.width}
                      height={comp.height}
                      borderColor={comp.borderColor}
                      borderRadius={comp.borderRadius}
                      backgroundColor={comp.backgroundColor}
                      shapeType={comp.shapeType}
                      shapeTypeColor={comp.shapeTypeColor}
                    />
                    {compResult && renderFeedback}
                  </ResizableComponent>
                );
              }

              if (comp.type === "toggle_button") {
                // NEW: Align with editor's path-render + inside-path toggle
                const userVal = answers[question.id]?.[comp.id];
                const toggled = userVal === undefined ? false : userVal;
                const opacity = comp.opacity ?? 1;

                // Helper: build path from anchors/handles
                const buildBezierPath = (anchors, handles) => {
                  const N = anchors?.length || 0;
                  if (N === 0 || handles?.length !== N) return "";
                  let d = `M ${anchors[0].x},${anchors[0].y} `;
                  for (let i = 0; i < N; i++) {
                    const curr = anchors[i];
                    const next = anchors[(i + 1) % N];
                    const ch = handles[i];
                    const nh = handles[(i + 1) % N];
                    if (!ch || !nh) continue;
                    const rOff = ch.right || { x: 0, y: 0 };
                    const lOff = nh.left || { x: 0, y: 0 };
                    d +=
                      `C ${curr.x + rOff.x},${curr.y + rOff.y} ` +
                      `${next.x + lOff.x},${next.y + lOff.y} ` +
                      `${next.x},${next.y} `;
                  }
                  return d + "Z";
                };

                const calculateTextCenter = (anchors, width, height) => {
                  if (!anchors || anchors.length === 0) {
                    return { x: width / 2, y: height / 2 };
                  }
                  const sumX = anchors.reduce((s, a) => s + a.x, 0);
                  const sumY = anchors.reduce((s, a) => s + a.y, 0);
                  return { x: sumX / anchors.length, y: sumY / anchors.length };
                };

                // Calculate bounds by sampling actual Bezier curve path
                const calculateContentBounds = (anchors, handles) => {
                  if (!anchors || anchors.length === 0) return null;

                  // Sample points along the Bézier curve to get accurate bounds
                  const samplePoints = [];
                  const numSamples = 50; // Number of points to sample along each curve segment
                  const N = anchors.length;

                  // For each Bezier curve segment
                  for (let i = 0; i < N; i++) {
                    const curr = anchors[i];
                    const next = anchors[(i + 1) % N];
                    const currentHandle = handles?.[i] || {
                      left: { x: 0, y: 0 },
                      right: { x: 0, y: 0 },
                    };
                    const nextHandle = handles?.[(i + 1) % N] || {
                      left: { x: 0, y: 0 },
                      right: { x: 0, y: 0 },
                    };

                    if (!currentHandle || !nextHandle) continue;

                    const rOff = currentHandle.right || { x: 0, y: 0 };
                    const lOff = nextHandle.left || { x: 0, y: 0 };

                    // Sample points along this Bézier curve segment
                    for (let t = 0; t <= 1; t += 1 / numSamples) {
                      // Bézier curve formula: B(t) = (1-t)³P₀ + 3(1-t)²tP₁ + 3(1-t)t²P₂ + t³P₃
                      const p0 = curr;
                      const p1 = { x: curr.x + rOff.x, y: curr.y + rOff.y };
                      const p2 = { x: next.x + lOff.x, y: next.y + lOff.y };
                      const p3 = next;

                      const t1 = 1 - t;
                      const x =
                        t1 * t1 * t1 * p0.x +
                        3 * t1 * t1 * t * p1.x +
                        3 * t1 * t * t * p2.x +
                        t * t * t * p3.x;
                      const y =
                        t1 * t1 * t1 * p0.y +
                        3 * t1 * t1 * t * p1.y +
                        3 * t1 * t * t * p2.y +
                        t * t * t * p3.y;

                      samplePoints.push({ x, y });
                    }
                  }

                  if (samplePoints.length === 0) return null;

                  // Calculate bounds from sampled points
                  let minX = samplePoints[0].x;
                  let minY = samplePoints[0].y;
                  let maxX = samplePoints[0].x;
                  let maxY = samplePoints[0].y;

                  samplePoints.forEach((point) => {
                    minX = Math.min(minX, point.x);
                    minY = Math.min(minY, point.y);
                    maxX = Math.max(maxX, point.x);
                    maxY = Math.max(maxY, point.y);
                  });

                  return { minX, minY, maxX, maxY };
                };

                let svgNode = null;
                let pathNode = null;
                const handleClickInside = (e) => {
                  // Toggle only if click is inside path
                  if (!svgNode || !pathNode) {
                    updateAnswer(question.id, comp.id, !toggled);
                    return;
                  }
                  try {
                    const pt = svgNode.createSVGPoint();
                    pt.x = e.clientX;
                    pt.y = e.clientY;
                    const ctm = svgNode.getScreenCTM();
                    if (!ctm) return;
                    const local = pt.matrixTransform(ctm.inverse());
                    let inside = false;
                    if (typeof pathNode.isPointInFill === "function") {
                      inside = pathNode.isPointInFill(local);
                    } else {
                      const bb = pathNode.getBBox();
                      inside =
                        local.x >= bb.x &&
                        local.x <= bb.x + bb.width &&
                        local.y >= bb.y &&
                        local.y <= bb.y + bb.height;
                    }
                    if (inside) {
                      updateAnswer(question.id, comp.id, !toggled);
                    }
                  } catch (_) {
                    // Fallback: toggle
                    updateAnswer(question.id, comp.id, !toggled);
                  }
                };

                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={defaultSizes.toggle_button.width}
                    defaultHeight={defaultSizes.toggle_button.height}
                    animationDelay={compIndex}
                  >
                    {/* OLD toggle_button rendering commented out above */}
                    <div
                      className="w-full h-full relative"
                      onClick={handleClickInside}
                      onMouseMove={(e) => {
                        try {
                          if (!svgNode || !pathNode) return;
                          const pt = svgNode.createSVGPoint();
                          pt.x = e.clientX;
                          pt.y = e.clientY;
                          const ctm = svgNode.getScreenCTM();
                          if (!ctm) return;
                          const local = pt.matrixTransform(ctm.inverse());
                          let inside = false;
                          if (typeof pathNode.isPointInFill === "function") {
                            inside = pathNode.isPointInFill(local);
                          } else {
                            const bb = pathNode.getBBox();
                            inside =
                              local.x >= bb.x &&
                              local.x <= bb.x + bb.width &&
                              local.y >= bb.y &&
                              local.y <= bb.y + bb.height;
                          }
                          e.currentTarget.style.cursor = inside
                            ? "pointer"
                            : "default";
                        } catch (_) {
                          e.currentTarget.style.cursor = "default";
                        }
                      }}
                    >
                      {(() => {
                        const bounds = calculateContentBounds(
                          comp.anchors,
                          comp.handles
                        );
                        const padding = 15;
                        let viewBoxWidth,
                          viewBoxHeight,
                          viewBoxX = 0,
                          viewBoxY = 0;
                        if (bounds) {
                          viewBoxWidth = Math.max(
                            bounds.maxX - bounds.minX + padding * 2,
                            50
                          );
                          viewBoxHeight = Math.max(
                            bounds.maxY - bounds.minY + padding * 2,
                            30
                          );
                          viewBoxX = bounds.minX - padding;
                          viewBoxY = bounds.minY - padding;
                        } else {
                          viewBoxWidth = comp.width || 100;
                          viewBoxHeight = comp.height || 40;
                        }
                        const textCenter = calculateTextCenter(
                          comp.anchors,
                          viewBoxWidth,
                          viewBoxHeight
                        );

                        return (
                          <svg
                            ref={(n) => (svgNode = n)}
                            width="100%"
                            height="100%"
                            viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`}
                            preserveAspectRatio="none"
                          >
                            <path
                              ref={(n) => (pathNode = n)}
                              d={buildBezierPath(comp.anchors, comp.handles)}
                              fill={
                                toggled
                                  ? comp.backgroundColor || "#ffffff"
                                  : "transparent"
                              }
                              stroke={comp.borderColor || "#000000"}
                              strokeWidth={comp.borderWidth || 2}
                              fillOpacity={opacity}
                            />
                            {(() => {
                              const fontFamily =
                                comp.fontFamily || "Arial, sans-serif";
                              const fontSize = comp.fontSize || 14;
                              const fontStyles = comp.fontStyles || {
                                bold: true,
                                italic: false,
                                underline: false,
                                lineThrough: false,
                              };
                              const fontColor =
                                comp.fontColor ||
                                (toggled ? "#2563eb" : "#6b7280");
                              const textDecoration = [
                                fontStyles?.underline ? "underline" : "",
                                fontStyles?.lineThrough ? "line-through" : "",
                              ]
                                .filter(Boolean)
                                .join(" ");
                              return (
                                <text
                                  x={textCenter.x}
                                  y={textCenter.y}
                                  textAnchor="middle"
                                  dominantBaseline="central"
                                  fill={fontColor}
                                  fontSize={fontSize}
                                  fontFamily={fontFamily}
                                  fontWeight={
                                    fontStyles?.bold ? "bold" : "normal"
                                  }
                                  fontStyle={
                                    fontStyles?.italic ? "italic" : "normal"
                                  }
                                  style={{
                                    pointerEvents: "none",
                                    userSelect: "none",
                                    textDecoration,
                                  }}
                                >
                                  {toggled ? "ON" : "OFF"}
                                </text>
                              );
                            })()}
                          </svg>
                        );
                      })()}
                    </div>
                    {compResult && renderFeedback}
                  </ResizableComponent>
                );
              }

              if (comp.type === "numeric_slider") {
                const val =
                  answers[question.id]?.[comp.id] ??
                  comp.currentValue ??
                  comp.minValue;
                const boxHeight = comp.height;

                console.log("numeric slider", comp, boxHeight);
                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={comp.width}
                    defaultHeight={boxHeight}
                    extraStyle={{ height: boxHeight }}
                    animationDelay={compIndex}
                    //  style={{ height: '200px !important' }}
                  >
                    <div className="">
                      <NumericSliderAnswer
                        value={val}
                        onChange={(newVal) =>
                          updateAnswer(question.id, comp.id, newVal)
                        }
                        minValue={comp.minValue}
                        maxValue={comp.maxValue}
                        targetValue={comp.targetValue}
                        mode={comp.mode}
                        className="w-full"
                        style={{
                          width: "100%",
                          height: boxHeight,
                          backgroundColor:
                            comp.optionBackgroundColor || "#f9fafb",
                          borderRadius: comp.optionBorderRadius,
                        }}
                        compValues={comp}
                      />
                      {compResult && renderFeedback}
                    </div>
                  </ResizableComponent>
                );
              }

              if (comp.type === "discrete_slider") {
                const val =
                  answers[question.id]?.[comp.id] ?? comp.selectedIndex ?? 0;
                console.log("discrete_slider", comp);
                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={comp.width}
                    defaultHeight={comp.height}
                    animationDelay={compIndex}
                  >
                    <div
                      className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4"
                      style={{
                        backgroundColor:
                          comp.optionBackgroundColor || "#f9fafb",
                        borderRadius: comp.optionBorderRadius,
                      }}
                    >
                      <DiscreteSliderAnswer
                        value={val}
                        onChange={(newVal) =>
                          updateAnswer(question.id, comp.id, newVal)
                        }
                        options={comp.options}
                        compValues={comp}
                      />
                      {compResult && renderFeedback}
                    </div>
                  </ResizableComponent>
                );
              }

              if (comp.type === "ranking") {
                const currentOrder =
                  answers[question.id]?.[comp.id] ||
                  getShuffledOrder(comp.items.length);
                console.log(comp, "ranking component");
                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={defaultSizes.ranking.width}
                    defaultHeight={defaultSizes.ranking.height}
                    extraStyle={{
                      height: comp.height - 52 + comp.optionBorderWidth * 2,
                    }}
                    animationDelay={compIndex}
                  >
                    <div
                      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col"
                      style={{
                        backgroundColor: comp.backgroundColor || "#ffffff",
                        borderColor: comp.optionBorderColor || "#e5e7eb",
                        borderWidth: comp.optionBorderWidth || 1,
                      }}
                    >
                      <div className="flex-1 overflow-auto p-2">
                        <RankingAnswer
                          items={comp.items || []}
                          currentOrder={currentOrder}
                          componentValue={comp}
                          onChange={(newOrder) =>
                            updateAnswer(question.id, comp.id, newOrder)
                          }
                        />
                      </div>
                      {compResult && renderFeedback}
                    </div>
                  </ResizableComponent>
                );
              }

              if (comp.type === "matching_pairs") {
                const currentMatches = answers[question.id]?.[comp.id] || [];
                console.log("matching_pairs", currentMatches);
                const compWidth =
                  comp.width || defaultSizes.matching_pairs.width;
                const compHeight =
                  comp.height || defaultSizes.matching_pairs.height;

                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={compWidth}
                    defaultHeight={compHeight}
                    animationDelay={compIndex}
                  >
                    {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-2 "
                      style={{
                          width: `${compWidth}px`,
                          height: `${compHeight}px`,
                        }}
                        
                        > */}
                    {/* <div
                        className="absolute inset-0"
                      
                      > */}
                    <MatchingPairsAnswer
                      pairs={comp.pairs}
                      value={currentMatches}
                      onChange={(newMatches) =>
                        updateAnswer(question.id, comp.id, newMatches)
                      }
                      containerWidth={compWidth}
                      containerHeight={compHeight}
                      fontSizeValue
                      optionClassName="bg-white  hover:bg-indigo-50 border-2 hover:border-indigo-300 transition-colors "
                      compValues={comp}
                    />
                    {/* </div> */}
                    {compResult && renderFeedback}
                    {/* </div> */}
                  </ResizableComponent>
                );
              }

              if (comp.type === "shape") {
                return (
                  <ResizableComponent
                    key={comp.id}
                    comp={comp}
                    defaultWidth={defaultSizes.shape.width}
                    defaultHeight={defaultSizes.shape.height}
                    animationDelay={compIndex}
                  >
                    <div className="bg-transparent w-full h-full">
                      <ShapeAnswer
                        shapeType={comp.shapeType || "rectangle"}
                        backgroundColor={comp.backgroundColor || "#4A90E2"}
                        borderRadius={comp.borderRadius}
                        opacity={comp.opacity || 1}
                        rotation={comp.rotation || 0}
                        borderWidth={comp.borderWidth || 0}
                        borderColor={comp.borderColor || "transparent"}
                        borderStyle={comp.borderStyle || "solid"}
                        className="w-full h-full"
                      />
                    </div>
                    {compResult && renderFeedback}
                  </ResizableComponent>
                );
              }

              return null;
            })}
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-6 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
        <div className="text-xs sm:text-sm text-gray-500">
          {attemptsLeft > 0 && !alreadyCorrect ? (
            <span className="flex items-center">
              <Clock size={14} className="mr-1" />
              {attemptsLeft} attempt{attemptsLeft !== 1 ? "s" : ""} left
            </span>
          ) : alreadyCorrect ? (
            <span className="flex items-center text-emerald-600">
              <Check size={14} className="mr-1" />
              Complete
            </span>
          ) : (
            <span className="flex items-center text-red-600">
              <X size={14} className="mr-1" />
              No attempts left
            </span>
          )}
        </div>

        <button
          onClick={() => handleSubmitQuestion(question.id)}
          disabled={submitDisabled || submittingQuestions[question.id]}
          className={`flex items-center gap-1 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium text-sm sm:text-base transition-all ${
            submitDisabled || submittingQuestions[question.id]
              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
              : "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700"
          }`}
        >
          {submittingQuestions[question.id] ? (
            <>
              <svg
                className="animate-spin h-3 w-3 sm:h-4 sm:w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                ></path>
              </svg>
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <span>Submit Answer</span>
              <ChevronRight size={8} className="sm:size-8" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default QuestionCard;
